import type { Locale } from "@salesboard/contracts";

/**
 * Plural selection, from `Intl.PluralRules` rather than from a hand-rolled `n === 1`.
 *
 * English needs two forms and Czech needs four, which is the whole reason this exists: a
 * ternary on `n === 1` produces "5 příležitosti" — wrong in a way an English speaker cannot
 * see and a Czech reader cannot unsee.
 *
 * The Czech categories, as ICU defines them:
 *
 * | n | category | example |
 * |---|---|---|
 * | 1 | `one` | 1 příležitost |
 * | 2, 3, 4 | `few` | 3 příležitosti |
 * | 1.5, 0.5 | `many` | 1,5 příležitosti |
 * | 0, 5, 11, 100 | `other` | 5 příležitostí |
 *
 * Note what that means: **`other` is the ≥5 form and `many` is the fractional one** — not the
 * other way round. Reading `many` as "lots" is the mistake this table exists to prevent, and
 * `plural.test.ts` pins every category so it cannot creep back in.
 */
export type PluralForms = {
  one: string;
  /** 2–4 in Czech. Falls back to `other` where a language does not distinguish it. */
  few?: string;
  /** Fractions in Czech — genitive singular, the same word `few` uses. Defaults to `few`. */
  many?: string;
  /** 0 and 5+ in Czech; everything but 1 in English. */
  other: string;
};

const rules = new Map<Locale, Intl.PluralRules>();

function rulesFor(locale: Locale): Intl.PluralRules {
  const existing = rules.get(locale);
  if (existing) return existing;
  const created = new Intl.PluralRules(locale);
  rules.set(locale, created);
  return created;
}

/** The right form of one word for `count`, in `locale`. */
export function pluralForm(locale: Locale, count: number, forms: PluralForms): string {
  switch (rulesFor(locale).select(count)) {
    case "one":
      return forms.one;
    case "few":
      return forms.few ?? forms.other;
    case "many":
      return forms.many ?? forms.few ?? forms.other;
    default:
      return forms.other;
  }
}

/**
 * A locale's plural selector, so a catalogue module names its own language once.
 *
 * `cs.ts` opens with `const p = pluralFor("cs")` and every counted phrase in it reads
 * `${n} ${p(n, { … })}` — which is what stops one entry from being written against the
 * wrong language's rules.
 */
export function pluralFor(locale: Locale): (count: number, forms: PluralForms) => string {
  return (count, forms) => pluralForm(locale, count, forms);
}
